import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, fade } from '@material-ui/core/styles';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@material-ui/core';
import { Warning as WarningIcon } from '@material-ui/icons';
import { DUPLICATE_TITLE_MESSAGE } from '../../../../utils/api/documenti_api';
// A date reader with nothing iot about it: `dd/mm/yyyy`, and `null` when the
// date is missing or unreadable, which is exactly the distinction needed here.
// A third copy of this formatting in the repo would add nothing.
import { formattaData } from '../iot/formatoData';

// The rhythm of the other dialogs of this section (FolderDialog, the attachment
// form, the delete confirmation): on MUI's own values this window would be the
// only one with sharper corners, uppercase labels and buttons resting on the
// bottom edge. Spacing in multiples of `theme.spacing`; the radii are the
// numbers this section already uses everywhere — 16 on the paper, 12 on panels,
// 10 on buttons — and that nobody has moved into the theme yet.
const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 3, 1.5),
  },
  badge: {
    alignItems: 'center',
    // The theme's yellow and the brown it keeps as its `contrastText`: the pair
    // reserved for warning surfaces. The red of the delete confirmation would
    // say something else — there a document is lost, here a choice is made
    // between two roads and neither is irreversible.
    backgroundColor: fade(theme.palette.warning.main, 0.5),
    borderRadius: 12,
    color: theme.palette.warning.contrastText,
    display: 'flex',
    flexShrink: 0,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: 700,
    letterSpacing: '-0.01em',
  },
  content: {
    padding: theme.spacing(1, 3, 2),
  },
  explanation: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    marginBottom: theme.spacing(2),
  },
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(2),
  },
  row: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  nextRow: {
    marginTop: theme.spacing(1.5),
  },
  label: {
    color: theme.palette.text.secondary,
    minWidth: 120,
  },
  value: {
    overflowWrap: 'anywhere',
  },
  actions: {
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 3, 2.5),
  },
  dismissButton: {
    borderRadius: 10,
    color: theme.palette.text.secondary,
    fontWeight: 500,
    padding: theme.spacing(1, 2.5),
    textTransform: 'none',
  },
  overwriteButton: {
    borderRadius: 10,
    boxShadow: 'none',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    textTransform: 'none',
    '&:hover': {
      boxShadow: `0 4px 12px ${fade(theme.palette.primary.main, 0.3)}`,
    },
  },
}));

/**
 * The confirmation for a title already taken. Both ways out are legitimate — the
 * operator can rename, and can also overwrite — so the sentence states a fact
 * rather than giving an order.
 *
 * «in questa cartella» carries the rest of it: without the folder scoping the
 * confirmation reads as though the title were reserved across the whole archive,
 * which it is not.
 *
 * The title of the document already there, and the day it was uploaded, sit in
 * the card and not in the prose: they are the two facts the choice is made on,
 * and they read worse inside a sentence.
 *
 * The two rows are label and value, with no icon in front: on this screen an
 * icon says what type a file is, what a button does or what kind of dialog this
 * is, and no value carries one — the dates in the list are bare text.
 *
 * The way out that is not overwriting is named by the caller, because it differs:
 * a save has a form to go back to and a title to change there («Rinomina»), while
 * a move only has itself to call off («Annulla»).
 */
export default function OverwriteAttachmentDialog({
  open,
  existingTitle,
  uploadedDate,
  saving,
  dismissLabel,
  onDismiss,
  onOverwrite,
}) {
  const classes = useStyles();

  // The caller drives openness and content from one piece of state, so both go
  // in the same commit while MUI is still fading the window out. Rendering the
  // last conflict through the exit keeps the card from emptying to «—» and the
  // button from changing its name on the way out.
  const lastShown = useRef({ existingTitle, uploadedDate, dismissLabel });
  if (open) {
    lastShown.current = { existingTitle, uploadedDate, dismissLabel };
  }
  const shown = open
    ? { existingTitle, uploadedDate, dismissLabel }
    : lastShown.current;

  const readableDate = formattaData(shown.uploadedDate);

  return (
    <Dialog
      open={open}
      // Not while the overwrite is in flight: Esc and the backdrop would take
      // the window away without stopping the write, and the operator would be
      // told nothing while the file they meant to keep is replaced. The two
      // buttons are already disabled for the same reason.
      onClose={saving ? undefined : onDismiss}
      disableBackdropClick={saving}
      disableEscapeKeyDown={saving}
      maxWidth="xs"
      fullWidth
      PaperProps={{ className: classes.paper }}
      aria-labelledby="overwrite-attachment-title"
    >
      <div className={classes.header}>
        <div className={classes.badge}>
          <WarningIcon />
        </div>
        <Typography id="overwrite-attachment-title" className={classes.title}>
          Titolo già in uso
        </Typography>
      </div>
      <DialogContent className={classes.content}>
        {/* One sentence, defined once: the field under the form shows the same
            one, and two copies would drift. */}
        <Typography variant="body2" className={classes.explanation}>
          {DUPLICATE_TITLE_MESSAGE}
        </Typography>
        <div className={classes.card}>
          <div className={classes.row}>
            <Typography variant="body2" className={classes.label}>
              Titolo
            </Typography>
            <Typography variant="body2" className={classes.value}>
              {shown.existingTitle || '—'}
            </Typography>
          </div>
          {/* No row rather than an empty one: the answer may carry no date, and
              «Data caricamento —» is one more question for the reader, not one
              fact less. */}
          {readableDate && (
            <div className={`${classes.row} ${classes.nextRow}`}>
              <Typography variant="body2" className={classes.label}>
                Data caricamento
              </Typography>
              <Typography variant="body2">{readableDate}</Typography>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onDismiss}
          disabled={saving}
          className={classes.dismissButton}
        >
          {shown.dismissLabel}
        </Button>
        <Button
          onClick={onOverwrite}
          disabled={saving}
          color="primary"
          variant="contained"
          className={classes.overwriteButton}
          style={{ color: '#fff' }}
          // While the second save is in flight the label gives way to the
          // spinner, and without these two the button loses its name at the very
          // moment there is something to announce.
          aria-label="Sovrascrivi"
          aria-busy={!!saving}
        >
          {saving ? (
            <CircularProgress size={20} style={{ color: '#fff' }} />
          ) : (
            'Sovrascrivi'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

OverwriteAttachmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  existingTitle: PropTypes.string,
  uploadedDate: PropTypes.string,
  saving: PropTypes.bool,
  dismissLabel: PropTypes.string.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onOverwrite: PropTypes.func.isRequired,
};
